<script lang="ts">
  import { defineStore, storeToRefs } from 'pinia'
  import { computed, ref } from 'vue'

  export interface SearchProviderProps {
    /** The default search context to use when no context is selected. */
    defaultSearchContext: SearchContextType
    /** The allowed search contexts. If not provided, all contexts are allowed. Otherwise, only true values are allowed. */
    allowedSearchContexts?: Partial<Record<SearchContextType, boolean>>
    /** The maximum number of recent searches to show. */
    maxRecentSearches?: number
    /** Where the state of the component is stored. */
    storeName?: string
  }

  function useComponentState(props: SearchProviderProps) {
    const _selectedSearchContext = ref(props.defaultSearchContext)

    function setSelectedSearchContext(context: SearchContextType) {
      if (
        props.allowedSearchContexts &&
        !props.allowedSearchContexts[context]
      ) {
        console.warn(`Context ${context} is not allowed.`)
        return
      }
      _selectedSearchContext.value = context
    }

    const selectedSearchContext = computed(() => _selectedSearchContext.value)

    const allowedSearchContexts = computed(() => props.allowedSearchContexts)

    const allowedSearchContextsMetadata = computed(
      (): Partial<Record<SearchContextType, SearchContextMetadata>> => {
        if (!props.allowedSearchContexts) {
          return searchContextTypesMapping
        }
        const items: Partial<Record<SearchContextType, SearchContextMetadata>> =
          {}
        Object.entries(props.allowedSearchContexts ?? {}).forEach(
          ([context, allowed]) => {
            const contextType = context as SearchContextType
            if (allowed) {
              items[contextType] = searchContextTypesMapping[contextType]
            }
          }
        )
        return items
      }
    )

    const user = useUser()

    const isSearching = ref(false)

    let activeAbortController: AbortController | null = null

    const searchInputValue = ref('')

    const lastSearchValue = ref<string | undefined>(undefined)

    const isSearchEmpty = computed(() => {
      return (
        searchInputValue.value === undefined ||
        searchInputValue.value === null ||
        searchInputValue.value === ''
      )
    })

    const debouncedSearch = useDebounceFn(
      async (search: string, context: SearchContextType) => {
        return await globalSearchFn(search, context, user)
      },
      500
    )

    const isSearchDirty = computed(
      () =>
        lastSearchValue.value !== searchInputValue.value && !isSearchEmpty.value
    )
    const createAbortPromise = (signal: AbortSignal) =>
      new Promise<never>((_, reject) => {
        const rejectWithAbort = () =>
          reject(new DOMException('Aborted', 'AbortError'))

        if (signal.aborted) {
          rejectWithAbort()
          return
        }

        signal.addEventListener('abort', rejectWithAbort, { once: true })
      })

    /**
     * The search results.
     * undefined when the search is not finished.
     * [] when the search is finished and there are no results.
     * [SearchResultItem...] when the search is finished and there are results.
     */
    const searchResults = ref<SearchResult[] | undefined>(undefined)

    const isEmptySearchResults = computed(
      () =>
        Array.isArray(searchResults.value) && searchResults.value.length === 0
    )
    const setSearchResults = (result: SearchResult[]) => {
      searchResults.value = result
    }
    const clearSearchResults = () => {
      searchResults.value = undefined
    }

    async function callSearch(search: string, context: SearchContextType) {
      activeAbortController?.abort('New search started')
      if (
        search === '' ||
        search === undefined ||
        search === null ||
        search.length === 0
      ) {
        return undefined
      }
      if (
        typeof search !== 'string' ||
        (typeof search === 'string' && search.length === 0)
      ) {
        return undefined
      }
      const abortController = new AbortController()
      activeAbortController = abortController
      searchInputValue.value = search
      setSelectedSearchContext(context)
      console.log('calling search', search, context)
      isSearching.value = true
      try {
        const results = await Promise.race([
          debouncedSearch(search, context),
          createAbortPromise(abortController.signal)
        ])
        if (!abortController.signal.aborted) {
          console.log('setting results', results)
          setSearchResults(results)
        }
        return results
      } catch (error) {
        if (
          error instanceof DOMException &&
          (error.name === 'AbortError' || error.message === 'Aborted')
        ) {
          return undefined
        }
        throw error
      } finally {
        if (activeAbortController === abortController) {
          isSearching.value = false
        }
      }
    }

    function clearSearch() {
      searchInputValue.value = ''
      setSelectedSearchContext(props.defaultSearchContext)
      clearSearchResults()
    }
    return {
      selectedSearchContext,
      setSelectedSearchContext,
      allowedSearchContexts,
      allowedSearchContextsMetadata,
      callSearch,
      searchInputValue,
      clearSearchResults,
      isSearching,
      lastSearchValue,
      isSearchDirty,
      isSearchEmpty,
      searchResults,
      setSearchResults,
      isEmptySearchResults,
      clearSearch
    }
  }

  function createComponentState(props: SearchProviderProps) {
    if (!props.storeName) {
      return useComponentState(props)
    }
    const store = defineStore(`gs-search-${props.storeName}`, () => {
      return useComponentState(props)
    })
    const storeInstance = store()
    const storeRefs = storeToRefs(storeInstance)

    // Return a normalized object that preserves reactivity
    // Functions come directly from the store, reactive values come from storeToRefs
    return {
      selectedSearchContext: storeRefs.selectedSearchContext,
      setSelectedSearchContext: storeInstance.setSelectedSearchContext,
      allowedSearchContexts: storeInstance.allowedSearchContexts,
      allowedSearchContextsMetadata:
        storeInstance.allowedSearchContextsMetadata,
      callSearch: storeInstance.callSearch,
      searchInputValue: storeRefs.searchInputValue,
      clearSearchResults: storeInstance.clearSearchResults,
      isSearching: storeRefs.isSearching,
      lastSearchValue: storeRefs.lastSearchValue,
      isSearchDirty: storeRefs.isSearchDirty,
      isSearchEmpty: storeRefs.isSearchEmpty,
      searchResults: storeRefs.searchResults,
      setSearchResults: storeInstance.setSearchResults,
      isEmptySearchResults: storeRefs.isEmptySearchResults,
      clearSearch: storeInstance.clearSearch
    }
  }
</script>

<script setup lang="ts">
  import type { SearchResult } from '../utils/search-types'
  import { unref } from 'vue'
  import {
    type SearchContextMetadata,
    searchContextTypesMapping,
    type SearchContextType
  } from '../utils/global-search-utils'
  import { GatTextEdit } from '@gat-ship/vue-components'
  import { useDebounceFn } from '@vueuse/core'
  import { useUser } from '@gat-ship/lib/user'
  import { globalSearchFn } from '../utils/global-search-utils'
  import {
    provideSearch,
    type SearchProviderContext
  } from '../utils/search-providers'

  const props = withDefaults(defineProps<SearchProviderProps>(), {
    maxRecentSearches: 5,
    componentState: 'component'
  })

  const emit = defineEmits<{
    (e: 'item-selected', item: SearchResult): void
  }>()

  const componentState = createComponentState(props)

  const searchInputRef = ref<InstanceType<typeof GatTextEdit> | null>(null)

  provideSearch({
    callSearch: componentState.callSearch,
    searchInputValue: componentState.searchInputValue,
    searchInputRef,
    clearSearchResults: componentState.clearSearchResults,
    isSearching: computed(() => unref(componentState.isSearching)),
    lastSearchValue: componentState.lastSearchValue,
    isSearchInputFocused: computed(
      () => searchInputRef.value?.isFocused ?? false
    ),
    isSearchInputDirty: computed(() => unref(componentState.isSearchDirty)),
    isSearchInputEmpty: computed(() => unref(componentState.isSearchEmpty)),
    defaultSearchContext: props.defaultSearchContext,
    selectedSearchContext: computed(() =>
      unref(componentState.selectedSearchContext)
    ),
    allowedSearchContexts: computed(
      () => unref(componentState.allowedSearchContexts) ?? {}
    ),
    allowedSearchContextsMetadata: computed(
      () => unref(componentState.allowedSearchContextsMetadata) ?? []
    ),
    setSelectedSearchContext: componentState.setSelectedSearchContext,
    searchResults: computed(() => unref(componentState.searchResults)),
    isEmptySearchResults: computed(() =>
      unref(componentState.isEmptySearchResults)
    ),
    setSearchResults: componentState.setSearchResults,
    clearSearch: componentState.clearSearch
  })

  defineExpose<SearchProviderContext>({
    callSearch: componentState.callSearch,
    searchInputValue: componentState.searchInputValue,
    searchInputRef,
    clearSearchResults: componentState.clearSearchResults,
    isSearching: computed(() => unref(componentState.isSearching)),
    lastSearchValue: componentState.lastSearchValue,
    isSearchInputFocused: computed(
      () => searchInputRef.value?.isFocused ?? false
    ),
    isSearchInputDirty: computed(() => unref(componentState.isSearchDirty)),
    isSearchInputEmpty: computed(() => unref(componentState.isSearchEmpty)),
    defaultSearchContext: props.defaultSearchContext,
    selectedSearchContext: computed(() =>
      unref(componentState.selectedSearchContext)
    ),
    allowedSearchContexts: computed(
      () => unref(componentState.allowedSearchContexts) ?? {}
    ),
    allowedSearchContextsMetadata: computed(
      () => unref(componentState.allowedSearchContextsMetadata) ?? []
    ),
    setSelectedSearchContext: componentState.setSelectedSearchContext,
    searchResults: computed(() => unref(componentState.searchResults)),
    isEmptySearchResults: computed(() =>
      unref(componentState.isEmptySearchResults)
    ),
    setSearchResults: componentState.setSearchResults,
    clearSearch: componentState.clearSearch
  })
</script>
<template>
  <slot />
</template>
